import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CATEGORIES, TOOL_ICONS, FALLBACK_ICON } from '@/lib/tools';
import { getToolContent } from '@/content/toolContent';
import { safeJsonLd } from '@/lib/jsonLd';
import OutputEditor from '@/components/OutputEditor';
import GrammarCheckPanel from '@/components/GrammarCheckPanel';
import SignaturePad from '@/components/SignaturePad';
import PdfPagePicker from '@/components/PdfPagePicker';
import {
  UploadSimple, X, ArrowRight, CheckCircle, FileArrowDown,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECENT_KEY = 'ff-recent-tools';

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function extOf(name) {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

export default function ToolPage() {
  const { toolId } = useParams();
  const [tool, setTool] = useState(null);
  const [maxMb, setMaxMb] = useState(100);
  const [files, setFiles] = useState([]);
  const [optionValues, setOptionValues] = useState({});
  const [status, setStatus] = useState('idle'); // idle | uploading | processing | done | error
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    // Reset local state whenever the user navigates to a different tool so the
    // drop zone (not a stale success-panel) is shown on the new page.
    setFiles([]);
    setResult(null);
    setStatus('idle');
    setProgress(0);
    setError('');
    setOptionValues({});

    (async () => {
      try {
        const [toolRes, cfgRes] = await Promise.all([
          axios.get(`${API}/tools/${toolId}`),
          axios.get(`${API}/config`).catch(() => ({ data: { max_upload_mb: 100 } })),
        ]);
        setTool(toolRes.data);
        setMaxMb(cfgRes.data.max_upload_mb || 100);
        // Dynamic SEO — update <title>, <meta description> and <link canonical> per tool
        try {
          const toolName = toolRes.data.name;
          document.title = `${toolName} — Free Online | Go Convert Documents`;
          const meta = document.querySelector('meta[name="description"]');
          if (meta) {
            meta.setAttribute(
              'content',
              `${toolRes.data.description} Free online ${toolName} tool — no signup, no watermark, files auto-delete after 1 hour. Works on Windows, Mac, Linux, iOS and Android.`
            );
          }
          const site = process.env.REACT_APP_SITE_URL || 'https://goconvertdocuments.com';
          const canonical = document.querySelector('link[rel="canonical"]');
          if (canonical) canonical.setAttribute('href', `${site.replace(/\/$/, '')}/tools/${toolId}`);
          const ogUrl = document.querySelector('meta[property="og:url"]');
          if (ogUrl) ogUrl.setAttribute('content', `${site.replace(/\/$/, '')}/tools/${toolId}`);
          const ogTitle = document.querySelector('meta[property="og:title"]');
          if (ogTitle) ogTitle.setAttribute('content', `${toolName} — Free Online | Go Convert Documents`);
        } catch (metaErr) {
          console.warn('Failed to update page meta tags:', metaErr);
        }
        // Initialize option defaults
        const defaults = {};
        (toolRes.data.options || []).forEach((o) => {
          if (o.default !== undefined) defaults[o.name] = o.default;
        });
        setOptionValues(defaults);
      } catch (e) {
        setError('Tool not found');
      }
    })();
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      const next = [toolId, ...list.filter((x) => x !== toolId)].slice(0, 6);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch (storageErr) {
      console.warn('Failed to persist recent tools to localStorage:', storageErr);
    }

    // Restore default meta on unmount
    return () => {
      document.title = 'Go Convert Documents — Free Online Document Converter';
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute(
          'content',
          'Convert any file format to PDF, Word, Excel, PowerPoint, and more. Fast, secure, no registration required.'
        );
      }
    };
  }, [toolId]);

  const accepted = tool ? tool.accept.split(',').map((e) => e.trim().toLowerCase()) : [];
  const maxBytes = maxMb * 1024 * 1024;

  const validateAndFilter = useCallback((arr) => {
    const ok = [];
    for (const f of arr) {
      const ext = extOf(f.name);
      if (accepted.length && !accepted.includes(ext)) {
        toast.error(`"${f.name}" is not a ${tool?.accept} file`);
        continue;
      }
      if (f.size > maxBytes) {
        toast.error(`"${f.name}" is larger than ${maxMb} MB`);
        continue;
      }
      ok.push(f);
    }
    return ok;
  }, [tool, maxBytes, maxMb]);

  const addFiles = useCallback((incoming) => {
    if (!tool) return;
    const arr = validateAndFilter(Array.from(incoming));
    if (!arr.length) return;
    if (!tool.multi) {
      setFiles(arr.slice(0, 1));
    } else {
      setFiles((prev) => [...prev, ...arr]);
    }
  }, [tool, validateAndFilter]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const validateOptions = () => {
    if (toolId === 'sign-pdf' && !String(optionValues.signature_b64 || '').trim()) {
      toast.error('Please draw or upload your signature first');
      return false;
    }
    if (toolId === 'remove-pages' && !String(optionValues.pages_to_remove || '').trim()) {
      toast.error('Select at least one page to remove');
      return false;
    }
    for (const o of tool.options || []) {
      if (o.required && !String(optionValues[o.name] || '').trim()) {
        toast.error(`${o.label} is required`);
        return false;
      }
    }
    return true;
  };

  const handleConvert = async () => {
    if (!files.length) {
      toast.error('Please add at least one file');
      return;
    }
    if (!validateOptions()) return;

    setStatus('uploading');
    setProgress(0);
    setError('');

    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    (tool.options || []).forEach((o) => {
      const v = optionValues[o.name];
      if (v !== undefined && v !== '') formData.append(o.name, v);
    });

    try {
      const res = await axios.post(`${API}/convert/${toolId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.min(60, Math.round((evt.loaded / evt.total) * 60));
            setProgress(pct);
          }
        },
      });
      setStatus('processing');
      for (let p = 60; p <= 100; p += 8) {
        setProgress(p);
        await new Promise((r) => setTimeout(r, 60));
      }
      setResult(res.data);
      setStatus('done');
      toast.success('Conversion complete!');
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || 'Conversion failed';
      setError(msg);
      setStatus('error');
      toast.error(msg);
    }
  };

  const reset = () => {
    setFiles([]);
    setResult(null);
    setStatus('idle');
    setProgress(0);
    setError('');
  };

  if (error && !tool) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <p className="font-display text-3xl">Tool not found</p>
        <Link to="/" className="brutal-btn mt-6 inline-block" data-testid="back-home-link">
          Back home
        </Link>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="h-8 w-40 bg-zinc-200 mx-auto animate-pulse" />
      </div>
    );
  }

  const cat = CATEGORIES[tool.category];
  const Icon = TOOL_ICONS[tool.id] || FALLBACK_ICON;

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="flex items-start gap-4 mb-10">
        <span className={`w-16 h-16 grid place-items-center border-2 border-black ${cat.badge} brutal-shadow-sm`}>
          <Icon weight="bold" size={32} className="text-black" />
        </span>
        <div>
          <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500">
            {cat.label}
          </p>
          <h1
            data-testid="tool-title"
            className="font-display font-bold text-4xl md:text-5xl leading-tight text-slate-950 mt-1"
          >
            {tool.name}
          </h1>
          <p className="text-zinc-600 mt-3 max-w-2xl">{tool.description}</p>
        </div>
      </div>

      {status === 'done' && result ? (
        <div
          data-testid="success-panel"
          className="border-2 border-black bg-white p-8 md:p-12 brutal-shadow-lg text-center"
        >
          <CheckCircle weight="fill" size={72} className="mx-auto text-emerald-500" />
          <h2 className="font-display font-bold text-4xl md:text-5xl mt-4">Done!</h2>
          <p className="text-zinc-600 mt-2">
            Your file is ready. It will auto-delete in 1 hour.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              data-testid="download-btn"
              href={`${BACKEND_URL}${result.download_url}`}
              download={result.filename}
              className="brutal-btn inline-flex items-center gap-2"
            >
              <FileArrowDown weight="bold" size={20} />
              Download {result.filename}
            </a>
            <button
              data-testid="convert-another-btn"
              onClick={reset}
              className="brutal-btn secondary"
            >
              Convert another
            </button>
          </div>

          <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 border-2 border-black text-xs font-mono-plex">
            <span className="uppercase tracking-widest text-zinc-500">Size</span>
            <span>{humanSize(result.size)}</span>
          </div>

          <OutputEditor result={result} onSaved={(next) => setResult(next)} />
          <GrammarCheckPanel result={result} />
        </div>
      ) : (
        <>
          {/* Dropzone */}
          <div
            data-testid="upload-zone"
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`min-h-[320px] p-8 flex flex-col items-center justify-center cursor-pointer border-4 ${
              dragOver ? 'border-solid border-black bg-zinc-100' : 'border-dashed border-zinc-400 bg-white'
            }`}
            style={{ transition: 'background-color 0.15s ease, border-color 0.15s ease' }}
          >
            <input
              ref={inputRef}
              data-testid="file-input"
              type="file"
              accept={tool.accept}
              multiple={tool.multi}
              hidden
              onChange={(e) => addFiles(e.target.files)}
            />
            <span className="w-16 h-16 grid place-items-center bg-yellow-300 border-2 border-black brutal-shadow-sm">
              <UploadSimple weight="bold" size={30} />
            </span>
            <p className="font-display font-semibold text-2xl md:text-3xl mt-6 text-center">
              {dragOver ? 'Drop the files' : 'Drop files here or click to browse'}
            </p>
            <p data-testid="upload-limits" className="text-sm text-zinc-600 mt-2 font-mono-plex">
              Accepts {tool.accept} · {tool.multi ? 'One or more files' : 'One file at a time'} · Up to {maxMb} MB each
            </p>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div data-testid="file-list" className="mt-6 space-y-2">
              {files.map((f, i) => (
                <div
                  key={`${f.name}-${i}`}
                  data-testid={`file-item-${i}`}
                  className="flex items-center justify-between border-2 border-black bg-white p-3 brutal-shadow-sm"
                >
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{f.name}</p>
                    <p className="text-xs font-mono-plex text-zinc-500">{humanSize(f.size)}</p>
                  </div>
                  <button
                    data-testid={`remove-file-${i}`}
                    onClick={() => removeFile(i)}
                    disabled={status !== 'idle' && status !== 'error'}
                    className="w-9 h-9 grid place-items-center border-2 border-black bg-white hover:bg-rose-500 hover:text-white disabled:opacity-40"
                    style={{ transition: 'background-color 0.15s ease, color 0.15s ease' }}
                    aria-label="Remove file"
                  >
                    <X weight="bold" size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Signature pad (only for sign-pdf tool) */}
          {toolId === 'sign-pdf' && (
            <div className="mt-6">
              <SignaturePad
                onChange={(b64) => setOptionValues((v) => ({ ...v, signature_b64: b64 }))}
              />
            </div>
          )}

          {/* PDF page picker (only for remove-pages tool) */}
          {toolId === 'remove-pages' && files[0] && (
            <PdfPagePicker
              file={files[0]}
              mode="delete"
              onChange={(kept) => {
                // Compute pages_to_remove = all page numbers NOT in kept
                // We don't know total from here; store kept and let backend compute? No —
                // send the list of pages the user wants to REMOVE by inverting locally
                // using the thumbnail count exposed by the picker component's DOM.
                const pickerThumbs = document.querySelectorAll("[data-testid^='page-thumb-']");
                const total = pickerThumbs.length;
                if (!total) return;
                const keptSet = new Set(kept);
                const toRemove = [];
                for (let i = 1; i <= total; i += 1) if (!keptSet.has(i)) toRemove.push(i);
                setOptionValues((v) => ({ ...v, pages_to_remove: toRemove.join(',') }));
              }}
            />
          )}

          {/* Options */}
          {tool.options && tool.options.length > 0 && (
            <div data-testid="tool-options" className="mt-6 border-2 border-black bg-white p-5 brutal-shadow-sm">
              <p className="font-mono-plex text-xs uppercase tracking-widest text-zinc-500 mb-3">
                Options
              </p>
              <div className="grid gap-4">
                {tool.options
                  // These are captured via richer UI widgets and hidden from
                  // the generic renderer.
                  .filter((o) => !(toolId === 'sign-pdf' && o.name === 'signature_b64'))
                  .filter((o) => !(toolId === 'remove-pages' && o.name === 'pages_to_remove'))
                  .map((o) => (
                  <div key={o.name}>
                    <label className="block text-sm font-semibold mb-1">
                      {o.label}
                      {o.required && <span className="text-rose-600 ml-1">*</span>}
                    </label>
                    {o.type === 'select' ? (
                      <select
                        data-testid={`option-${o.name}`}
                        value={optionValues[o.name] ?? o.default ?? ''}
                        onChange={(e) => setOptionValues((v) => ({ ...v, [o.name]: e.target.value }))}
                        className="w-full px-3 py-2 border-2 border-black bg-white font-mono-plex text-sm focus:outline-none"
                      >
                        {(o.choices || []).map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        data-testid={`option-${o.name}`}
                        type={o.type === 'password' ? 'password' : o.type === 'number' ? 'number' : 'text'}
                        value={optionValues[o.name] ?? ''}
                        onChange={(e) => setOptionValues((v) => ({ ...v, [o.name]: e.target.value }))}
                        placeholder={o.default ? String(o.default) : ''}
                        className="w-full px-3 py-2 border-2 border-black bg-white font-mono-plex text-sm focus:outline-none"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progress */}
          {(status === 'uploading' || status === 'processing') && (
            <div className="mt-6" data-testid="progress-panel">
              <div className="flex items-center justify-between mb-2">
                <p className="font-mono-plex text-xs uppercase tracking-widest">
                  {status === 'uploading' ? 'Uploading' : 'Converting'}
                </p>
                <p
                  data-testid="progress-percent"
                  className="font-mono-plex text-xs font-semibold"
                >
                  {progress}%
                </p>
              </div>
              <div
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                className="h-6 border-2 border-black bg-white overflow-hidden"
              >
                <div
                  className="h-full progress-shimmer"
                  style={{ width: `${progress}%`, transition: 'width 0.15s ease' }}
                />
              </div>
            </div>
          )}

          {/* Error */}
          {status === 'error' && error && (
            <div
              data-testid="error-panel"
              className="mt-6 border-2 border-black bg-rose-100 p-4 brutal-shadow-sm"
            >
              <p className="font-semibold text-rose-900">Conversion failed</p>
              <p className="text-sm text-rose-800 mt-1 font-mono-plex">{error}</p>
            </div>
          )}

          {/* Action */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs font-mono-plex text-zinc-500 uppercase tracking-widest">
              {files.length} file{files.length === 1 ? '' : 's'} ready
            </p>
            <button
              data-testid="convert-btn"
              onClick={handleConvert}
              disabled={files.length === 0 || status === 'uploading' || status === 'processing'}
              className="brutal-btn inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Convert now <ArrowRight weight="bold" size={18} />
            </button>
          </div>
        </>
      )}

      {/* SEO-rich content below the fold */}
      <section data-testid="tool-seo-content" className="mt-20 border-t-2 border-black pt-12">
        {(() => {
          const rich = getToolContent(tool.id);
          if (!rich) return null;
          const siteUrl = process.env.REACT_APP_SITE_URL || 'https://goconvertdocuments.com';
          return (
            <>
              <p className="text-zinc-700 leading-relaxed text-lg" data-testid="tool-intro">
                {rich.intro}
              </p>

              <h2 className="font-display font-bold text-3xl md:text-4xl mt-14 text-slate-950">
                Common ways to use {tool.name}
              </h2>
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                {rich.useCases.map((u) => (
                  <div key={u.title} className="border-2 border-black bg-white p-5 brutal-shadow-sm">
                    <h3 className="font-display font-semibold text-lg text-slate-950">{u.title}</h3>
                    <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{u.text}</p>
                  </div>
                ))}
              </div>

              <h2 className="font-display font-bold text-3xl md:text-4xl mt-14 text-slate-950">
                {tool.name} — frequently asked questions
              </h2>
              <div className="mt-6 space-y-3">
                {rich.faqs.map((f, i) => (
                  <details
                    key={f.q}
                    data-testid={`tool-faq-${i}`}
                    className="border-2 border-black bg-white p-5 brutal-shadow-sm"
                  >
                    <summary className="cursor-pointer font-semibold">{f.q}</summary>
                    <p className="text-zinc-700 mt-3 leading-relaxed text-sm">{f.a}</p>
                  </details>
                ))}
              </div>

              <h2 className="font-display font-bold text-3xl md:text-4xl mt-14 text-slate-950">
                Pro tips for {tool.name}
              </h2>
              <ul className="mt-6 space-y-3">
                {rich.tips.map((t) => (
                  <li key={t} className="border-l-4 border-black bg-zinc-50 pl-4 py-2 text-zinc-800 text-sm leading-relaxed">
                    {t}
                  </li>
                ))}
              </ul>

              {/* JSON-LD: FAQPage + BreadcrumbList for rich results */}
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: safeJsonLd({
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: rich.faqs.map((f) => ({
                      '@type': 'Question',
                      name: f.q,
                      acceptedAnswer: { '@type': 'Answer', text: f.a },
                    })),
                  }),
                }}
              />
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: safeJsonLd({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
                      { '@type': 'ListItem', position: 2, name: 'Tools', item: `${siteUrl}/#all-tools` },
                      { '@type': 'ListItem', position: 3, name: tool.name, item: `${siteUrl}/tools/${tool.id}` },
                    ],
                  }),
                }}
              />
            </>
          );
        })()}

        <h2 className="font-display font-bold text-3xl md:text-4xl text-slate-950 mt-14">
          How to use {tool.name}
        </h2>
        <ol className="mt-6 space-y-3 text-zinc-700 leading-relaxed">
          <li><strong>1. Upload</strong> — drop your {tool.accept} file(s) into the box above or click to browse.</li>
          <li><strong>2. Configure</strong> — set any tool options (password, angle, watermark text) if applicable.</li>
          <li><strong>3. Convert</strong> — click <em>Convert now</em>. Processing usually takes 3–10 seconds.</li>
          <li><strong>4. Download</strong> — save the result to your device. Your file will auto-delete from our servers in 1 hour.</li>
        </ol>

        <h2 className="font-display font-bold text-3xl md:text-4xl mt-14 text-slate-950">
          Why use {tool.name}?
        </h2>
        <div className="grid md:grid-cols-3 gap-5 mt-6">
          <div className="border-2 border-black bg-white p-5 brutal-shadow-sm">
            <h3 className="font-display font-semibold text-lg">Free &amp; unlimited</h3>
            <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
              No signup, no daily limit, no watermark on the output.
            </p>
          </div>
          <div className="border-2 border-black bg-white p-5 brutal-shadow-sm">
            <h3 className="font-display font-semibold text-lg">Private by default</h3>
            <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
              HTTPS upload, auto-delete after 1 hour, zero tracking of your document contents.
            </p>
          </div>
          <div className="border-2 border-black bg-white p-5 brutal-shadow-sm">
            <h3 className="font-display font-semibold text-lg">Works everywhere</h3>
            <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
              Windows, macOS, Linux, Android, iOS — anywhere with a modern browser.
            </p>
          </div>
        </div>

        <h2 className="font-display font-bold text-3xl md:text-4xl mt-14 text-slate-950">
          Related tools
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {[
            ['pdf-to-word', 'PDF to Word'],
            ['word-to-pdf', 'Word to PDF'],
            ['merge-pdf', 'Merge PDF'],
            ['compress-pdf', 'Compress PDF'],
            ['pdf-to-jpg', 'PDF to JPG'],
            ['jpg-to-pdf', 'JPG to PDF'],
            ['ocr-pdf', 'OCR PDF'],
            ['protect-pdf', 'Protect PDF'],
          ]
            .filter(([id]) => id !== tool.id)
            .slice(0, 6)
            .map(([id, label]) => (
              <Link
                key={id}
                to={`/tools/${id}`}
                data-testid={`related-tool-${id}`}
                className="border-2 border-black bg-white px-4 py-3 text-sm font-semibold brutal-shadow-sm hover:brutal-shadow"
                style={{ transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
              >
                {label}
              </Link>
            ))}
        </div>
      </section>
    </div>
  );
}
